
/**
 * Copilot Utility Functions
 */

import { Route } from "react-router-dom";
import { CopilotAttachment } from "./types";

/**
 * Get knowledge about specific platform features
 */
export const getFeatureKnowledge = (featureId: string): string => {
  const featureInfo: Record<string, string> = {
    'valuation': `
      Our Valuation Calculator helps you estimate your startup's value using multiple methodologies:
      1. Comparable Company Analysis - Based on valuations of similar startups
      2. Scorecard Method - Adjusts average valuation based on founder experience, product readiness, etc.
      3. Venture Capital Method - Projects future value and calculates backward
      4. Discounted Cash Flow - For startups with revenue history

      To access this feature, navigate to the Valuation Calculator from your dashboard.
    `,
    'investor-matching': `
      Our investor matching algorithm helps you find investors most likely to be interested in your startup.
      The system uses your startup profile, industry, stage, and funding needs to match you with relevant investors.
      
      Features include:
      - AI-powered matching based on investment history
      - Filter by investment size, industry focus, and location
      - Detailed investor profiles and contact information (Pro feature)
      
      To get the most accurate matches, complete your startup profile with detailed information.
    `,
    'pitch-deck': `
      Our platform offers two key tools for pitch decks:
      
      1. Pitch Deck Generator - Create a professional pitch deck by answering questions about your startup
      2. Pitch Analyzer - Get AI feedback on your existing pitch deck
      
      The Pitch Deck Generator helps you create a compelling story with proper structure and content.
      The Pitch Analyzer provides feedback on design, content, messaging, and investor appeal.
      
      Both tools are accessible from your dashboard under the Pitch section.
    `,
    'crm': `
      Our Investor CRM helps you track relationships with potential investors. Key features include:
      
      - Contact management for investors
      - Meeting and follow-up tracking
      - Relationship status tracking (e.g., contacted, meeting scheduled, due diligence)
      - Email template library for investor communications
      
      Premium users get additional features like email integration and advanced analytics.
    `,
  };
  
  return featureInfo[featureId] || '';
};

/**
 * Get context information about the current platform location
 */
export const getPlatformContext = (location: any): string => {
  const pathMap: Record<string, string> = {
    '/investor-directory': `
      You are on the Investor Directory page. This feature helps users find and filter investors 
      based on criteria like industry, stage, and investment size. Users can view investor profiles 
      and (with premium access) get contact information.
    `,
    '/valuation-calculator': `
      You are on the Valuation Calculator page. This tool helps founders estimate their startup's 
      valuation using multiple methodologies including Comparable Analysis, Scorecard Method, 
      and Venture Capital Method.
    `,
    '/pitch-analyzer': `
      You are on the Pitch Analyzer page. This tool allows users to upload their pitch deck and 
      receive AI-generated feedback on content, design, structure, and investor appeal.
    `,
    '/pitch-deck-generator': `
      You are on the Pitch Deck Generator page. This AI-powered tool creates customized pitch 
      decks based on information provided about the user's startup.
    `,
    '/investor-crm': `
      You are on the Investor CRM page. This tool helps track investor relationships, 
      communications, and follow-ups during the fundraising process.
    `,
    '/dashboard': `
      You are on the Dashboard page. This shows an overview of fundraising progress, 
      recommended actions, and quick access to key platform features.
    `,
    '/ai-coaching': `
      You are on the AI Fundraising Advisor page. This feature provides personalized 
      fundraising advice and answers to specific questions.
    `,
    '/': `
      You are on the Home/Landing page. This introduces the platform's key features 
      and benefits for startup founders.
    `,
  };
  
  const pathname = location.pathname || '/';
  return pathMap[pathname] || `You are on the ${pathname} page of our platform.`;
};

/**
 * Get contextual help for specific situations
 */
export const getContextualHelp = (context: string): string => {
  const contextualHelp: Record<string, string> = {
    'new_user': `
      Welcome to the platform! Here are some ways I can help you:
      
      1. Navigate to key features using the sidebar menu
      2. Set up your startup profile to get personalized recommendations
      3. Explore our investor directory to find potential investors
      4. Create or analyze your pitch deck
      5. Calculate your startup's valuation
      
      What would you like to explore first?
    `,
    'premium_upgrade': `
      Upgrading to premium gives you access to:
      
      1. Investor contact information
      2. Advanced investor filters and matching
      3. Unlimited AI pitch deck analysis
      4. Advanced valuation tools
      5. Email templates and CRM features
      
      Would you like to learn more about our subscription plans?
    `,
    'valuation_help': `
      To get the most accurate valuation:
      
      1. Provide accurate financial data if available
      2. Select the most appropriate valuation methods for your stage
      3. Consider the industry-specific factors
      4. Use multiple methods to establish a valuation range
      
      Do you need help with a specific part of the valuation process?
    `,
  };
  
  return contextualHelp[context] || '';
};

/**
 * Get welcome prompts for the AI Copilot
 * Can optionally filter by path to provide page-specific prompts
 */
export const getWelcomePrompts = (path?: string): string => {
  // Base welcome messages for different pages
  const pathMap: Record<string, string> = {
    '/investor-directory': 'I can help you find investors that match your startup. What type of investors are you looking for?',
    '/valuation-calculator': 'I can help you understand different valuation methods. Would you like guidance on how to value your startup?',
    '/pitch-analyzer': 'I can help you improve your pitch deck. Would you like advice on content, design, or messaging?',
    '/pitch-deck-generator': 'I can help you create a compelling pitch deck. What information would you like to include?',
    '/investor-crm': 'I can help you manage investor relationships. Need tips on follow-ups or meeting preparation?',
    '/dashboard': 'Welcome to your fundraising dashboard! I can help you navigate the platform or answer fundraising questions.',
    '/ai-coaching': 'I\'m your AI fundraising advisor. Ask me any questions about your fundraising strategy!',
  };
  
  // If a path is provided and we have a specific message for it, return that
  if (path && pathMap[path]) {
    return pathMap[path];
  }
  
  // Default welcome message
  return 'How can I help with your fundraising today? You can ask me about investor matching, pitch decks, or valuation.';
};

/**
 * Convert a file to base64 for storage
 */
export const fileToBase64 = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Simulate text streaming for smooth UI experience
 */
export const simulateTextStreaming = (
  messageId: string,
  content: string,
  updateCallback: (id: string, content: string, isStreaming: boolean) => void,
  completeCallback: () => void,
  cancelRef?: React.MutableRefObject<boolean>
): void => {
  // Split the content into chunks that look like natural language segments
  const chunks = content.match(/[^.!?]+[.!?]+|\n+|[^.!?]+$/g) || [content];
  
  // Start with an empty display
  let displayContent = '';
  let chunkIndex = 0;
  
  // Determine an appropriate delay based on content length
  const baseDelay = 30; // ms per character
  const minDelay = 300; // minimum ms between chunks
  const maxDelay = 1200; // maximum ms between chunks
  
  const streamNextChunk = () => {
    // Check if streaming was cancelled
    if (cancelRef && cancelRef.current) {
      // If cancelled, show full content immediately and complete
      updateCallback(messageId, content, false);
      completeCallback();
      return;
    }
    
    // If we've processed all chunks, complete
    if (chunkIndex >= chunks.length) {
      updateCallback(messageId, displayContent, false);
      completeCallback();
      return;
    }
    
    // Add the next chunk to display content
    const chunk = chunks[chunkIndex];
    displayContent += chunk;
    
    // Update the display
    updateCallback(messageId, displayContent, true);
    
    // Calculate delay for next chunk based on length
    const chunkLength = chunk.length;
    const delay = Math.min(
      maxDelay, 
      Math.max(minDelay, chunkLength * baseDelay)
    );
    
    // Move to next chunk after delay
    chunkIndex++;
    setTimeout(streamNextChunk, delay);
  };
  
  // Start streaming
  setTimeout(streamNextChunk, 300); // Initial delay
};
